import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, apiClient } from '../../lib/api';
import type {
  Interaction,
  Partner,
  RelationshipDimensions,
  UhnwProfile,
} from '../../lib/types';
import { toast } from '../../lib/toast';
import styles from './Partners.module.css';

interface ProfilePayload {
  partner: Partner;
  interactions: Interaction[];
  dimensions?: RelationshipDimensions;
  uhnw?: UhnwProfile;
  ai_summary?: string;
}

// Backend response shape for GET /api/v1/crm/partners/{id} — Partner with eager-loaded relations.
type BackendPartnerDetail = Partner & {
  organization?: unknown;
  dimensions?: RelationshipDimensions | null;
  uhnw_profile?: UhnwProfile | null;
  recent_interactions?: Interaction[];
  open_exchanges?: unknown[];
  related_persons?: unknown[];
  tags?: unknown[];
};

function adaptBackendPayload(raw: BackendPartnerDetail): ProfilePayload {
  return {
    partner: raw,
    interactions: raw.recent_interactions ?? [],
    dimensions: raw.dimensions ?? undefined,
    uhnw: raw.uhnw_profile ?? undefined,
    ai_summary: raw.profile_summary ?? undefined,
  };
}

export function PartnerProfilePage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<ProfilePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await apiClient.get<BackendPartnerDetail>(
          `/api/v1/crm/partners/${id}`,
        );
        // Backend returns flat Partner with eager-loaded relations; adapt to nested ProfilePayload.
        setData(adaptBackendPayload(res));
      } catch (e) {
        if (e instanceof ApiError) {
          setErr(e.message);
          if (e.status >= 500) toast.error('Не удалось загрузить профиль.');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return <main className={styles.profile}><p className={styles.muted}>загружаем…</p></main>;
  }
  if (err || !data) {
    return (
      <main className={styles.profile}>
        <p className={styles.muted}>{err ?? 'не найдено'}</p>
        <Link to="/partners" className={styles.back}>← к картотеке</Link>
      </main>
    );
  }

  const p = data.partner;
  const dimSum = data.dimensions
    ? data.dimensions.common_interests_score +
      data.dimensions.emotional_empathy_score +
      data.dimensions.shared_circle_score
    : 0;

  return (
    <main className={styles.profile}>
      <Link to="/partners" className={styles.back}>← картотека</Link>

      <article className={styles.profileGrid}>
        <aside className={styles.profileSidebar}>
          <div className={styles.stamp}>
            <div className={styles.stampLabel}>Партнёр сети</div>
            <h1 className={styles.stampName}>
              {p.full_name_masked || p.full_name || '—'}
            </h1>
            {p.organization_id && (
              <div className={styles.stampOrg}>организация · {p.organization_id}</div>
            )}
            <div className={styles.strips}>
              <Strip label="Близость" value={p.intimacy_level} max={5} accent="oxblood" />
              <Strip label="Важность" value={p.importance} max={5} accent="gold" />
              <Strip
                label="Температура"
                value={Math.round(p.temperature / 20)}
                max={5}
                accent="warm"
              />
            </div>
          </div>

          {(p.function_tags?.length ?? 0) > 0 && (
            <div className={styles.sideBlock}>
              <div className={styles.sideLabel}>Роль в сети</div>
              <div className={styles.cardTags}>
                {(p.function_tags ?? []).map((t) => (
                  <span key={t} className={styles.tag}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {p.important_dates && Object.keys(p.important_dates).length > 0 && (
            <div className={styles.sideBlock}>
              <div className={styles.sideLabel}>Важные даты</div>
              <ul className={styles.dates}>
                {Object.entries(p.important_dates).map(([k, v]) => (
                  <li key={k}>
                    <span className={styles.dateK}>{k}</span>
                    <span className={styles.dateV}>{Array.isArray(v) ? v.join(', ') : v}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>

        <section className={styles.profileMain}>
          {data.ai_summary && (
            <div className={styles.summary}>
              <div className={styles.eyebrow}>AI-резюме</div>
              <p>{data.ai_summary}</p>
            </div>
          )}

          <section className={styles.profileSection}>
            <h2 className={styles.h2}>
              <span className={styles.hNum}>i.</span> Хроника
            </h2>
            {(data.interactions ?? []).length === 0 ? (
              <p className={styles.muted}>пока ничего не записано.</p>
            ) : (
              <ol className={styles.timeline}>
                {(data.interactions ?? []).map((it) => (
                  <li key={it.id} className={styles.timelineItem}>
                    <div className={styles.tlWhen}>
                      {new Date(it.happened_at).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </div>
                    <div className={styles.tlType}>{it.type}</div>
                    {it.summary && <p className={styles.tlSummary}>{it.summary}</p>}
                    {it.next_step && (
                      <p className={styles.tlNext}>
                        <em>дальше:</em> {it.next_step}
                        {it.next_step_due_at &&
                          ` · к ${new Date(it.next_step_due_at).toLocaleDateString('ru-RU')}`}
                      </p>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </section>

          {data.dimensions && (
            <section className={styles.profileSection}>
              <h2 className={styles.h2}>
                <span className={styles.hNum}>ii.</span> Три ножки отношений
              </h2>
              <div className={styles.dims}>
                <Dim
                  label="Общие интересы"
                  value={data.dimensions.common_interests_score}
                />
                <Dim
                  label="Эмоциональная эмпатия"
                  value={data.dimensions.emotional_empathy_score}
                />
                <Dim
                  label="Общий круг"
                  value={data.dimensions.shared_circle_score}
                />
              </div>
              <div className={styles.dimSum}>сумма {dimSum} / 15</div>
            </section>
          )}

          {data.uhnw && (
            <section className={styles.profileSection}>
              <h2 className={styles.h2}>
                <span className={styles.hNum}>iii.</span> UHNW-портрет
              </h2>
              <dl className={styles.uhnw}>
                {data.uhnw.estimated_aum_range && (
                  <UhnwRow k="оценка AUM" v={data.uhnw.estimated_aum_range} />
                )}
                {data.uhnw.source_of_wealth && (
                  <UhnwRow k="источник капитала" v={data.uhnw.source_of_wealth} />
                )}
                {data.uhnw.residences?.length ? (
                  <UhnwRow k="резиденции" v={data.uhnw.residences.join(' · ')} />
                ) : null}
                {data.uhnw.lifestyle_markers?.length ? (
                  <UhnwRow k="маркеры стиля" v={data.uhnw.lifestyle_markers.join(' · ')} />
                ) : null}
              </dl>
            </section>
          )}

          <section className={styles.profileSection}>
            <h2 className={styles.h2}>
              <span className={styles.hNum}>iv.</span> Граф связей
            </h2>
            <p className={styles.muted}>
              Визуализация графа появится в /navigator. Здесь — только список упоминаний.
            </p>
          </section>
        </section>
      </article>
    </main>
  );
}

function Strip({
  label,
  value,
  max,
  accent,
}: {
  label: string;
  value: number;
  max: number;
  accent: 'oxblood' | 'gold' | 'warm';
}) {
  return (
    <div className={styles.strip}>
      <div className={styles.stripLabel}>{label}</div>
      <div className={styles.stripDots}>
        {Array.from({ length: max }).map((_, i) => (
          <span
            key={i}
            className={`${styles.dot} ${i < value ? styles[`dot_${accent}`] : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

function Dim({ label, value }: { label: string; value: number }) {
  return (
    <div className={styles.dim}>
      <div className={styles.dimLabel}>{label}</div>
      <div className={styles.dimVal}>{value} / 5</div>
    </div>
  );
}

function UhnwRow({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  );
}
